import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { toast } from "sonner";
import { Percent, Plus, Pencil, Trash2, Info } from "lucide-react";
import type { CommissionRule } from "@/lib/types";
import { useTranslation } from "react-i18next";

const VERTICAL_OPTIONS = ["all", "parcel", "food", "grocery", "pharmacy", "other"];
const TYPE_OPTIONS: CommissionRule["type"][] = ["percentage", "fixed"];

interface RuleForm {
  name: string;
  vertical: string;
  type: CommissionRule["type"];
  rate: string;
  minAmount: string;
  maxAmount: string;
  active: boolean;
}

const EMPTY_FORM: RuleForm = {
  name: "",
  vertical: "all",
  type: "percentage",
  rate: "5",
  minAmount: "0",
  maxAmount: "",
  active: true,
};

export function AdminCommissionRules() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionRule | null>(null);
  const [form, setForm] = useState<RuleForm>(EMPTY_FORM);
  const [deleting, setDeleting] = useState<CommissionRule | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "commission-rules"],
    queryFn: () => api.admin.commissionRules(),
  });
  const rules = data?.data?.rules ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "commission-rules"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    qc.invalidateQueries({ queryKey: ["admin", "analytics"] });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (rule: CommissionRule) => {
    setEditing(rule);
    setForm({
      name: rule.name || "",
      vertical: rule.vertical || "all",
      type: rule.type,
      rate: rule.rate != null ? String(rule.rate) : "",
      minAmount: rule.minAmount != null ? String(rule.minAmount) : "0",
      maxAmount: rule.maxAmount != null ? String(rule.maxAmount) : "",
      active: rule.active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("admin.commissionRules.nameRequired"));
    const rate = parseFloat(form.rate);
    if (isNaN(rate) || rate < 0) return toast.error(t("admin.commissionRules.validRateRequired"));
    if (form.type === "percentage" && rate > 100) return toast.error(t("admin.commissionRules.percentageCap"));
    setSaving(true);
    const body: Partial<CommissionRule> = {
      name: form.name.trim(),
      vertical: form.vertical,
      type: form.type,
      rate,
      minAmount: parseFloat(form.minAmount || "0") || 0,
      maxAmount: form.maxAmount ? parseFloat(form.maxAmount) : null,
      active: form.active,
    };
    const res = editing
      ? await api.admin.updateCommissionRule(editing.id, body)
      : await api.admin.createCommissionRule(body);
    setSaving(false);
    if (!res.ok) return toast.error(res.error || t("admin.commissionRules.saveRuleFailed"));
    toast.success(editing ? t("admin.commissionRules.ruleUpdatedToast") : t("admin.commissionRules.ruleCreatedToast"));
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    invalidate();
  };

  const toggleActive = async (rule: CommissionRule) => {
    const res = await api.admin.updateCommissionRule(rule.id, { active: !rule.active });
    if (!res.ok) return toast.error(res.error);
    toast.success(rule.active ? t("admin.commissionRules.ruleDisabledToast") : t("admin.commissionRules.ruleEnabledToast"));
    invalidate();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const res = await api.admin.deleteCommissionRule(deleting.id);
    if (!res.ok) return toast.error(res.error || t("admin.commissionRules.deleteRuleFailed"));
    toast.success(t("admin.commissionRules.ruleDeletedToast"));
    setDeleting(null);
    invalidate();
  };

  // Form is reset by openCreate / openEdit before each open, so no
  // reset-on-close effect is needed.

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.commissionRules.loadRulesFailed")} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.commissionRules.title")}
        subtitle={t("admin.commissionRules.subtitle", { count: rules.length })}
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" /> {t("admin.commissionRules.createRule")}
          </Button>
        }
      />

      {/* Hint */}
      <Card className="border-sky-200/60 bg-sky-50/40 dark:border-sky-900/40 dark:bg-sky-950/20">
        <div className="flex items-start gap-2 p-3 text-xs text-sky-900 dark:text-sky-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">{t("admin.commissionRules.hintPercentage")}</span> {t("admin.commissionRules.hintPercentDesc")}{" "}
            <span className="font-semibold">{t("admin.commissionRules.hintFixed")}</span> {t("admin.commissionRules.hintFixedDesc")} <span className="font-mono">{t("admin.commissionRules.hintAllVertical")}</span> {t("admin.commissionRules.hintAllVerticalDesc")}
          </p>
        </div>
      </Card>

      {rules.length === 0 ? (
        <EmptyState
          icon={<Percent className="h-8 w-8" />}
          title={t("admin.commissionRules.noRulesYet")}
          description={t("admin.commissionRules.noRulesDesc")}
          action={
            <Button size="sm" onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" /> {t("admin.commissionRules.createRule")}
            </Button>
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.commissionRules.colName")}</TableHead>
                <TableHead>{t("admin.commissionRules.colVertical")}</TableHead>
                <TableHead>{t("admin.commissionRules.colType")}</TableHead>
                <TableHead className="text-right">{t("admin.commissionRules.colRate")}</TableHead>
                <TableHead className="text-right">{t("admin.commissionRules.colMin")}</TableHead>
                <TableHead className="text-right">{t("admin.commissionRules.colMax")}</TableHead>
                <TableHead>{t("admin.commissionRules.colActive")}</TableHead>
                <TableHead className="text-right">{t("admin.commissionRules.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell className="font-medium">{rule.name}</TableCell>
                  <TableCell className="capitalize">
                    {rule.vertical === "all" ? <Badge variant="secondary">{t("admin.commissionRules.hintAllVertical")}</Badge> : rule.vertical}
                  </TableCell>
                  <TableCell className="capitalize">{rule.type}</TableCell>
                  <TableCell className="text-right">
                    {rule.type === "percentage"
                      ? `${rule.rate}%`
                      : rule.rate}
                  </TableCell>
                  <TableCell className="text-right">{rule.minAmount ?? 0}</TableCell>
                  <TableCell className="text-right">{rule.maxAmount ?? "—"}</TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.active}
                      onCheckedChange={() => toggleActive(rule)}
                      aria-label={t("admin.commissionRules.toggleAria", { name: rule.name })}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(rule)}
                        aria-label={t("admin.commissionRules.editAria")}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => setDeleting(rule)}
                        aria-label={t("admin.commissionRules.deleteAria")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? t("admin.commissionRules.editCommissionRule") : t("admin.commissionRules.createCommissionRule")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="r-name">{t("admin.commissionRules.nameLabel")}</Label>
              <Input
                id="r-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("admin.commissionRules.namePlaceholder")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="r-vertical">{t("admin.commissionRules.verticalLabel")}</Label>
                <select
                  id="r-vertical"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.vertical}
                  onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                >
                  {VERTICAL_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v === "all" ? t("admin.commissionRules.allOption") : v}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-type">{t("admin.commissionRules.typeLabel")}</Label>
                <select
                  id="r-type"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm capitalize"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as CommissionRule["type"] })}
                >
                  {TYPE_OPTIONS.map((tt) => (
                    <option key={tt} value={tt}>{tt}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="r-rate">{form.type === "percentage" ? t("admin.commissionRules.rateLabelPercent") : t("admin.commissionRules.rateLabelFixed")}</Label>
                <Input
                  id="r-rate"
                  type="number"
                  step="0.1"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-min">{t("admin.commissionRules.minLabel")}</Label>
                <Input
                  id="r-min"
                  type="number"
                  value={form.minAmount}
                  onChange={(e) => setForm({ ...form, minAmount: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-max">{t("admin.commissionRules.maxLabel")}</Label>
                <Input
                  id="r-max"
                  type="number"
                  value={form.maxAmount}
                  onChange={(e) => setForm({ ...form, maxAmount: e.target.value })}
                  placeholder={t("admin.commissionRules.maxPlaceholder")}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch
                id="r-active"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
              <Label htmlFor="r-active" className="cursor-pointer text-sm">
                {t("admin.commissionRules.activeLabel")}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("admin.commissionRules.cancel")}</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? t("admin.commissionRules.saving") : editing ? t("admin.commissionRules.updateRule") : t("admin.commissionRules.createRule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.commissionRules.deleteRuleTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("admin.commissionRules.deleteRuleDesc", { name: deleting?.name })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>{t("admin.commissionRules.cancel")}</Button>
            <Button variant="destructive" onClick={confirmDelete}>{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
